<template>
    <Card :title="$t('exitDeclaration.exitDeclarationTable')">
        <div class="card-body">
            <button class="btn btn-link" @click="$refs.modal.show()">
                <i class="icon-plus3 mr-2"></i>
                {{ $t('buttons.create') }}
            </button>
        </div>
        <DataTable
            ref="DataTable"
            :config="table.config"
            :disabled-actions="table.selectedValues.length === 0"
            @select:action="selectAction"
            checkbox-key="uuid"
            v-model:selected="table.selectedValues"
        >
            <template #cell-id="{item}">
                <Paragraph>
                    <template #title>
                        <router-link :to="{ name: 'EditExitDeclarationPage', params: { exitDeclarationUuid: item.uuid } }">{{ item.id }}</router-link>
                    </template>
                    <template #description>
                        <span v-if="item.creatable?.name !== null">
                            <span>{{ $t('exitDeclaration.createFromAlert') }}: </span>
                            <router-link v-if="item.creatable?.name === 'inventory' && item.creatable.uuid" :to="{ name: 'EditInventoryPage', params: { inventoryUuid: item.creatable.uuid }}" class="text-teal font-weight-semibold cursor-pointer">{{ `${$t('labels.inventory')} № ${item.creatable?.id}` }}</router-link>
                            <span v-else-if="item.creatable?.name === 'inventory'">{{ `${$t('labels.inventory')} № ${item.creatable?.id}` }}</span>
                            <router-link v-else-if="item.creatable?.name === 'integration-order' && item.creatable.uuid" :to="{ name: 'MainInformation', params: { integrationUuid: item.creatable.uuid }}" class="text-teal font-weight-semibold cursor-pointer">{{ $t('labels.integration') }}</router-link>
                            <span v-else-if="item.creatable?.name === 'integration-order'">{{ $t('labels.integration') }}</span>
                        </span>
                        <span v-else>{{ $t('exitDeclaration.createdManualAlert') }}</span>
                    </template>
                </Paragraph>
            </template>
            <template #cell-status="{item}">
                <span class="badge" :style="{ background: item.status.id, color: 'white' }">{{ item.status.name }}</span>
            </template>
            <template #cell-product_items_sum="{item}">
                <Paragraph>
                    <template #title>
                        <Tooltip trigger="mouseenter focus">
                            <Tooltip trigger="click" @onShow="fetchProducts(item.uuid)" @onHide="productsString = ''">
                                <template #default>
                                    <span class="text-indigo cursor-pointer"><u>{{ Price.factory(item.product_items_sum).toCurrencyString(item.currency_code) }}</u></span>
                                </template>
                                <template #content>
                                    <div class="tippy-body" style="overflow: hidden;">
                                        <span style="white-space: nowrap;">
                                            <ol class="list mb-0" v-html="productsString"></ol>
                                        </span>
                                    </div>
                                </template>
                            </Tooltip>
                            <template #content>
                                <div class="tippy-body">
                                    {{ $t('alerts.click_to_show_product_name') }}
                                </div>
                            </template>
                        </Tooltip>
                    </template>
                </Paragraph>
            </template>
            <template #cell-created_at="{item}">
                <span>{{ DateFormatter.formatDateTime(item.created_at) }}</span>
            </template>
        </DataTable>
    </Card>

    <CreateExitDeclarationModal ref="modal" @created="$refs.DataTable.load()"/>
    <ConfirmeModal
        ref="ConfirmeDeleteModal"
        :title="$t('labels.removal')"
        :text="$t('exitDeclaration.deleteConfirmAlert')"
        @confirmed="deleteExitDeclaration"
    />
    <AppModal ref="ChangeExitDeclarationStatus" :title="$t('labels.status_change')" size="sm">
        <div class="modal-body">
            <Select
                :model-value="selectedExitDeclarationStatus?.uuid"
                :selected-value="selectedExitDeclarationStatus?.name"
                @update:model-value="selectedExitDeclarationStatus = $event"
                :options="exitDeclarationStatuses"
            />
        </div>
        <div class="modal-footer">
            <Button color="teal" @click="changeExitDeclarationStatus">
                <span>{{ $t('buttons.change') }}</span>
            </Button>
        </div>
    </AppModal>
</template>
<script>
import Card from '../../../../components/new/CardComponent.vue';
import DataTable from '../../../../components/new/datatable/DatatableComponent.vue';
import pageData from './data.json';
import Button from '../../../../components/new/button/AlphaButton.vue';
import Tooltip from '../../../../components/TooltipComponent.vue';
import Paragraph from '../../../../components/table/ParagraphComponent.vue';
import AppModal from '../../../../components/new/modal/AppModal.vue';
import Select from '../../../../components/new/form/select/AppSelect.vue';
import ConfirmeModal from '../../../../components/new/modal/ConfirmeModal.vue';
import CreateExitDeclarationModal from './modals/CreateExitDeclarationModal.vue';
import Price from '../../../../services/Price/Price';
import DateFormatter from '../../../../services/DateFormatter';
import { httpClient } from '../../../../services/HttpClient';
import { chunk } from '../../../../utils';
export default {
    name: "IndexExitDeclarationPage",
    components: {
        Card, DataTable, Button, Tooltip, Paragraph, AppModal, Select,
        ConfirmeModal, CreateExitDeclarationModal
    },
    data() {
        return {
            DateFormatter,
            Price: Price,
            table: {
                config: pageData.table,
                selectedValues: []
            },
            productsString: "",
            exitDeclarationStatuses: [],
            selectedExitDeclarationStatus: null
        }
    },
    methods: {
        async fetchProducts(uuid) {
            await httpClient.get('/management/exit-declarations/products', {
                exit_declaration_uuids: [uuid]
            }).then(response => {
                this.productsString = response.data.data.map(item => `<li>${item.product.name}</li>`).join('')
            })
        },
        selectAction(action) {
            if (action === 'delete') {
                this.$refs.ConfirmeDeleteModal.show();
            }

            if (action === 'changeStatus') {
                httpClient.get('/management/exit-declaration-statuses', { limit: 0 }).then(response => {
                    this.exitDeclarationStatuses = response.data.data;
                    this.$refs.ChangeExitDeclarationStatus.show();
                })
            }
        },
        async deleteExitDeclaration() {
            this.$refs.ConfirmeDeleteModal.hide();

            let requestData = this.table.selectedValues.map(uuid => ({
                method: 'DELETE',
                path: `/management/exit-declarations/${uuid}`
            }))

            await Promise.all(chunk(requestData, 10).map(chunk => httpClient.post('/batch', { data: chunk })))

            this.table.selectedValues = [];
            this.$refs.DataTable.load();
        },
        async changeExitDeclarationStatus() {
            this.$refs.ChangeExitDeclarationStatus.hide();

            let requestData = this.table.selectedValues.map(uuid => ({
                method: 'PUT',
                path: `/projects/${this.$route.params.projectUuid}/management/exit-declarations/${uuid}/update-status`,
                body: { status_id: this.selectedExitDeclarationStatus.id }
            }))

            await Promise.all(chunk(requestData, 10).map(chunk => httpClient.post('/batch', { data: chunk })))

            this.table.selectedValues = [];
            this.$refs.DataTable.load();
        }
    },
    watch: {
        '$route.query.template'(newTemplate) {
            this.table.config.templateUuid = newTemplate ?? null;
            this.$refs.DataTable.reload();
        }
    },
    mounted() {
        this.$refs.DataTable.load();
    },
    beforeMount() {
        this.table.config.templateUuid = this.$route.query.template;
    },
    beforeUnmount() {
        this.table.config.filters.forEach((filter) => {
            filter.default = 0;
        });
        this.table.config.templateUuid = null;
    }
}
</script>

<style scoped>
ol {
    padding-inline-start: 20px;
}
</style>
